<template>
  <div class="worklog-stream-page">
    <WelcomeBanner title="工作日志实时动态" :subtitle="'实时查看工作日志的创建和更新'" />
    
    <BaseCard>
      <div class="stream-header">
        <div class="header-left">
          <el-select v-model="filterStatus" placeholder="状态过滤" size="small" class="status-filter">
            <el-option label="全部" value="" />
            <el-option label="执行中" value="1" />
            <el-option label="成功" value="2" />
            <el-option label="失败" value="3" />
          </el-select>
          <el-input
            v-model="searchKeyword"
            placeholder="搜索日志内容"
            size="small"
            class="search-input"
            prefix-icon="el-icon-search"
          />
        </div>
        <div class="header-right">
          <el-button type="primary" size="small" @click="refreshLogs">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
        </div>
      </div>
      
      <div class="stream-content">
        <div v-if="loading" class="loading-wrapper">
          <i class="el-icon-loading"></i>
          <p>加载中...</p>
        </div>
        
        <div v-else-if="logs.length === 0" class="empty-wrapper">
          <i class="el-icon-info"></i>
          <p>暂无工作日志</p>
        </div>
        
        <div v-else class="log-list">
          <div 
            v-for="log in filteredLogs" 
            :key="log.log_id"
            class="log-item"
            :class="{ 'log-item-updated': log.isUpdated }"
          >
            <div class="log-header">
              <div class="log-info">
                <span class="log-action">{{ log.action_desc }}</span>
                <el-tag 
                  size="small" 
                  :type="getStatusType(log.status)"
                  class="log-status"
                >
                  {{ getStatusText(log.status) }}
                </el-tag>
              </div>
              <div class="log-time">
                {{ formatTime(log.start_time) }}
                <span v-if="log.end_time" class="log-duration">
                  ({{ formatDuration(log.cost_ms) }})
                </span>
              </div>
            </div>
            <div class="log-details">
              <div class="log-meta">
                <span class="meta-item">机器人: {{ log.robot_id }}</span>
                <span class="meta-item">项目: {{ log.project_id }}</span>
                <span v-if="log.task_id" class="meta-item">任务: {{ log.task_id }}</span>
              </div>
              <div v-if="log.result_summary" class="log-result">
                <span class="result-label">结果: </span>
                <span class="result-content">{{ log.result_summary }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import WelcomeBanner from '@/components/WelcomeBanner'
import BaseCard from '@/components/BaseCard'
import { getMyWorkLogList } from '@/api/workLog'
import { getWebSocketClient } from '@/utils/websocket'
import { wsBaseURL } from '@/config'

export default {
  name: 'WorkLogStream',
  components: {
    WelcomeBanner,
    BaseCard
  },
  data() {
    return {
      logs: [],
      loading: false,
      filterStatus: '',
      searchKeyword: '',
      wsClient: null
    }
  },
  computed: {
    filteredLogs() {
      let result = [...this.logs]
      
      // 状态过滤
      if (this.filterStatus) {
        result = result.filter(log => log.status === parseInt(this.filterStatus))
      }
      
      // 关键词搜索
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        result = result.filter(log => 
          log.action_desc.toLowerCase().includes(keyword) ||
          (log.result_summary && log.result_summary.toLowerCase().includes(keyword))
        )
      }
      
      // 按时间倒序排序
      return result.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
    }
  },
  mounted() {
    this.loadLogs()
    this.initWebSocket()
  },
  beforeDestroy() {
    if (this.wsClient) {
      this.wsClient.disconnect()
    }
  },
  methods: {
    async loadLogs() {
      this.loading = true
      try {
        const response = await getMyWorkLogList({
          page: 1,
          page_size: 5
        })
        if (response && response.list) {
          this.logs = response.list
        }
      } catch (error) {
        console.error('加载工作日志失败:', error)
        this.$message.error('加载工作日志失败')
      } finally {
        this.loading = false
      }
    },
    
    refreshLogs() {
      this.loadLogs()
    },
    
    initWebSocket() {
      // 打印环境变量配置，检查是否正确加载
      console.log('[WorkLogStream] 当前环境变量配置:')
      console.log('[WorkLogStream] wsBaseURL:', wsBaseURL)
      console.log('[WorkLogStream] process.env.NODE_ENV:', process.env.NODE_ENV)
      
      const userInfo = JSON.parse(localStorage.getItem('userInfo'))
      if (!userInfo || !userInfo.user_id) {
        console.error('用户未登录，无法建立WebSocket连接。')
        return
      }

      const user_id = userInfo.user_id
      const token = localStorage.getItem('token')

      this.wsClient = getWebSocketClient({
        url: wsBaseURL || 'ws://localhost:3020/api/v1/ws/connect',
        token: token,
        onOpen: () => {
          console.log('WebSocket连接成功')
          // 订阅工作日志频道
          this.wsClient.subscribe(`worklog:${user_id}`, this.handleWorkLogUpdate)
        },
        onClose: () => {
          console.log('WebSocket连接关闭')
        },
        onError: (error) => {
          console.error('WebSocket连接错误:', error)
        },
        onMessage: (data) => {
          console.log('[WebSocket] 收到全局消息:', data)
          if (data.type === 'worklog_update') {
            this.handleWorkLogUpdate(data)
          }
        }
      })

      this.wsClient.connect()
    },
    
    handleWorkLogUpdate(data) {
      if (data.type === 'worklog_update' && data.data) {
        const action = data.data.action;
        const worklog = data.data.worklog;
        if (action === 'create') {
          this.logs.unshift(worklog)
          if (this.logs.length > 100) {
            this.logs = this.logs.slice(0, 100)
          }
        } else if (action === 'update') {
          const index = this.logs.findIndex(log => log.log_id === worklog.log_id)
          if (index !== -1) {
            this.logs[index] = { ...worklog, isUpdated: true }
            setTimeout(() => {
              if (this.logs[index]) {
                this.logs[index].isUpdated = false
              }
            }, 3000)
          }
        }
      }
    },
    
    getStatusType(status) {
      const typeMap = {
        1: 'info',
        2: 'success',
        3: 'danger'
      }
      return typeMap[status] || 'info'
    },
    
    getStatusText(status) {
      const textMap = {
        1: '执行中',
        2: '成功',
        3: '失败'
      }
      return textMap[status] || '未知'
    },
    
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return date.toLocaleString()
    },
    
    formatDuration(ms) {
      if (!ms || ms < 0) return ''
      if (ms < 1000) {
        return `${ms}ms`
      } else if (ms < 60000) {
        return `${(ms / 1000).toFixed(1)}s`
      } else {
        const minutes = Math.floor(ms / 60000)
        const seconds = Math.floor((ms % 60000) / 1000)
        return `${minutes}m ${seconds}s`
      }
    }
  }
}
</script>

<style scoped lang="scss">
.worklog-stream-page {
  .stream-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);
    
    .header-left {
      display: flex;
      gap: var(--spacing-sm);
      
      .status-filter {
        width: 120px;
      }
      
      .search-input {
        width: 200px;
      }
    }
  }
  
  .stream-content {
    min-height: 400px;
    
    .loading-wrapper,
    .empty-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-2xl);
      color: var(--text-secondary);
      
      i {
        font-size: 48px;
        margin-bottom: var(--spacing-md);
        color: var(--border-color-base);
      }
    }
    
    .log-list {
      .log-item {
        padding: var(--spacing-md);
        border-bottom: 1px solid var(--border-color-lighter);
        transition: all 0.3s ease;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          background-color: var(--bg-hover);
        }
        
        &.log-item-updated {
          background-color: var(--success-light);
          animation: pulse 3s ease-in-out;
        }
        
        .log-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-sm);
          
          .log-info {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            
            .log-action {
              font-weight: 500;
              color: var(--text-primary);
            }
            
            .log-status {
              flex-shrink: 0;
            }
          }
          
          .log-time {
            font-size: var(--font-size-xs);
            color: var(--text-secondary);
            
            .log-duration {
              margin-left: var(--spacing-xs);
              font-style: italic;
            }
          }
        }
        
        .log-details {
          .log-meta {
            display: flex;
            gap: var(--spacing-md);
            font-size: var(--font-size-xs);
            color: var(--text-secondary);
            margin-bottom: var(--spacing-xs);
            
            .meta-item {
              &:before {
                content: '•';
                margin-right: var(--spacing-xs);
                color: var(--text-placeholder);
              }
              
              &:first-child:before {
                display: none;
              }
            }
          }
          
          .log-result {
            font-size: var(--font-size-sm);
            color: var(--text-regular);
            
            .result-label {
              font-weight: 500;
              color: var(--text-primary);
            }
          }
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 1;
  }
}
</style>